import React from 'react'
import { Paper, Stack, Text, TextInput, Button, Group, Box, Badge, ActionIcon } from '@mantine/core'
import { IconSearch, IconX, IconHistory } from '@tabler/icons-react'
import { useTheme } from '../../components/ThemeProvider'
import { isPC } from '../../utils/deviceUtil'
import type { SearchHistory } from '../utils/searchHistory'

interface SearchSectionProps {
  phone: string
  setPhone: (phone: string) => void
  onSearch: (phoneNumber?: string) => void
  onReset: () => void
  onClearHistory: () => void
  searchHistory: SearchHistory[]
  loading: boolean
}

export default function SearchSection({
  phone,
  setPhone,
  onSearch,
  onReset,
  onClearHistory,
  searchHistory,
  loading,
}: SearchSectionProps) {
  const theme = useTheme()
  const isPCDevice = isPC()

  return (
    <Paper
      p={isPCDevice ? 'xl' : 'lg'}
      radius="lg"
      shadow="sm"
      style={{
        background: `linear-gradient(135deg, ${theme.backgroundColor} 0%, rgba(255,255,255,0.9) 100%)`,
        border: `2px solid ${theme.surfaceBorderColor}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          background: `radial-gradient(circle, ${theme.primaryColor}20 0%, transparent 70%)`,
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <Box
        style={{
          position: 'absolute',
          bottom: -40,
          left: -40,
          width: 120,
          height: 120,
          background: `radial-gradient(circle, ${theme.primaryColor}15 0%, transparent 70%)`,
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <Stack gap={isPCDevice ? 'lg' : 'md'} style={{ position: 'relative', zIndex: 1 }}>
        <Group gap="sm" align="center">
          <Box
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: `${theme.primaryColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconSearch size={20} color={theme.primaryColor} />
          </Box>
          <div>
            <Text size={isPCDevice ? 'xl' : 'lg'} fw={700} c="gray.8">
              推广报告查询工具
            </Text>
            <Text size={isPCDevice ? 'sm' : 'xs'} c="dimmed" fw={500}>
              快速查询代理商推广授权情况
            </Text>
          </div>
        </Group>

        <TextInput
          placeholder="请输入手机号"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          maxLength={11}
          size={isPCDevice ? 'lg' : 'md'}
          leftSection={<IconSearch size={18} />}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              onSearch()
            }
          }}
          styles={{
            input: {
              borderColor: theme.surfaceBorderColor,
              '&:focus': {
                borderColor: theme.primaryColor,
              },
            },
          }}
        />

        <Group gap="sm">
          <Button
            onClick={() => onSearch()}
            disabled={loading}
            size={isPCDevice ? 'md' : 'sm'}
            style={{
              backgroundColor: theme.primaryColor,
            }}
          >
            查询
          </Button>
          <Button variant="light" onClick={onReset} disabled={loading} size={isPCDevice ? 'md' : 'sm'}>
            清空
          </Button>
          {searchHistory.length > 0 && (
            <Button
              variant="subtle"
              onClick={onClearHistory}
              disabled={loading}
              size={isPCDevice ? 'md' : 'sm'}
              leftSection={<IconHistory size={16} />}
            >
              清除历史
            </Button>
          )}
        </Group>

        {searchHistory.length > 0 && (
          <div>
            <Text size="xs" c="dimmed" mb="xs" fw={500}>
              最近搜索
            </Text>
            <Group gap="xs">
              {searchHistory.map((item) => (
                <Badge
                  key={item.phone}
                  size={isPCDevice ? 'lg' : 'md'}
                  variant="light"
                  color="gray"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  rightSection={
                    <ActionIcon
                      size="xs"
                      color="gray"
                      variant="transparent"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClearHistory()
                      }}
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  }
                  onClick={() => onSearch(item.phone)}
                >
                  {item.phone}
                </Badge>
              ))}
            </Group>
          </div>
        )}
      </Stack>
    </Paper>
  )
}
